import { Position } from './Position';
import { TerritoryState } from './Territory';
export var SideStatus;
(function (SideStatus) {
    SideStatus["ACTIVE"] = "ACTIVE";
    SideStatus["SUSPENDED"] = "SUSPENDED";
    SideStatus["ELIMINATED"] = "ELIMINATED";
    SideStatus["REANIMATED"] = "REANIMATED";
})(SideStatus || (SideStatus = {}));
/**
 * Конфигурация стартовых позиций для 10 сторон
 */
export const SIDE_START_POSITIONS = {
    1: new Position(0, 10), // нижний левый угол (начинает игру)
    2: new Position(0, 6), // левый средний
    3: new Position(0, 2), // левый верхний
    4: new Position(2, 0), // верхний левый
    5: new Position(6, 0), // верхний средний
    6: new Position(10, 0), // верхний правый угол
    7: new Position(10, 4), // правый верхний
    8: new Position(10, 8), // правый нижний
    9: new Position(8, 10), // нижний правый
    0: new Position(4, 10), // нижний средний
};
/**
 * Side Entity
 * Представляет игровую сторону (от 1 до 0)
 */
export class Side {
    _id;
    _playerId;
    _status;
    _territories; // Position keys
    _territoriesControlled; // Position keys of controlled territories
    _activeFields; // Постоянные активные поля
    _turnActiveFields; // Активные поля текущего раунда
    _movesInCurrentRound;
    _isBot;
    _lastMovePosition = null; // Последняя позиция хода
    _capturedOnLastMove = new Set(); // Территории захваченные окружением на последнем ходу
    _trapCapturedFields = new Set(); // Territories received via trap resolution — persists across moves within a round
    _pendingTraps = new Map(); // Отложенные ESCAPABLE ловушки (key = position.toKey())
    _pendingGroupConquests = []; // Отложенные групповые захваты
    _permanentlyOut = false; // Сдался / выбыл по неактивности — элиминирован БЕЗ права на реанимацию
    constructor(_id, playerId = null, isBot = false) {
        this._id = _id;
        if (_id < 0 || _id > 9) {
            throw new Error(`Invalid side id: ${_id}. Must be 0-9`);
        }
        this._playerId = playerId;
        this._status = SideStatus.ELIMINATED; // Start eliminated by default
        this._territories = new Set();
        this._territoriesControlled = new Set();
        this._activeFields = new Set();
        this._turnActiveFields = new Set();
        this._movesInCurrentRound = 0;
        this._isBot = isBot;
        // Добавляем стартовую позицию
        const startPos = SIDE_START_POSITIONS[_id];
        if (startPos) {
            const key = startPos.toKey();
            this._territories.add(key);
            this._territoriesControlled.add(key);
            // Don't set active fields yet - will be set during initialize()
        }
    }
    get id() {
        return this._id;
    }
    get playerId() {
        return this._playerId;
    }
    get status() {
        return this._status;
    }
    get territories() {
        return new Set(this._territories);
    }
    get activeFields() {
        return new Set(this._activeFields);
    }
    get turnActiveFields() {
        return new Set(this._turnActiveFields);
    }
    get movesInCurrentRound() {
        return this._movesInCurrentRound;
    }
    get isBot() {
        return this._isBot;
    }
    get isEliminated() {
        return this._status === SideStatus.ELIMINATED;
    }
    get isActive() {
        // REANIMATED mapped to ACTIVE for backward compatibility
        return this._status === SideStatus.ACTIVE || this._status === SideStatus.REANIMATED;
    }
    get isSuspended() {
        return this._status === SideStatus.SUSPENDED;
    }
    get isPermanentlyOut() {
        return this._permanentlyOut;
    }
    /**
     * НОВОЕ: Получить поля, которые будут активны в следующем раунде
     * Это lastMovePosition + capturedOnLastMove
     * Используется для визуального отображения "откуда игрок сможет ходить в следующем раунде"
     */
    getNextRoundActiveFields() {
        const fields = new Set();
        if (this._lastMovePosition) {
            const key = this._lastMovePosition.toKey();
            // Добавляем только если территория всё ещё принадлежит нам
            if (this._territories.has(key)) {
                fields.add(key);
            }
        }
        for (const capturedKey of this._capturedOnLastMove) {
            // Добавляем только если территория всё ещё принадлежит нам
            if (this._territories.has(capturedKey)) {
                fields.add(capturedKey);
            }
        }
        return fields;
    }
    /**
     * Захват территории
     */
    captureTerritory(position) {
        const key = position.toKey();
        this._territories.add(key);
        this._territoriesControlled.add(key);
        // Добавляем в список захваченных на последнем ходу
        this._capturedOnLastMove.add(key);
    }
    /**
     * Потеря территории
     */
    loseTerritory(position) {
        const key = position.toKey();
        this._territories.delete(key);
        this._territoriesControlled.delete(key);
        this._activeFields.delete(key);
        this._turnActiveFields.delete(key);
        // ВАЖНО: Также удаляем из списка захваченных на последнем ходу
        this._capturedOnLastMove.delete(key);
        // Если это была последняя позиция хода, сбрасываем её
        if (this._lastMovePosition && this._lastMovePosition.toKey() === key) {
            this._lastMovePosition = null;
        }
    }
    /**
     * Является ли поле ходовым (активным) для этой стороны ПРЯМО СЕЙЧАС.
     *
     * Единственный источник правды о «ходовом поле». Опирается на
     * _turnActiveFields — тот же набор, который использует MovementValidator,
     * чтобы разрешить ход, и который клиент рисует фигурой (isActiveField: true).
     *
     * ВАЖНО: НЕ _activeFields. Тот набор урезается до последней клетки приземления
     * на каждом ходу (clearActiveFieldsForSide), поэтому в середине хода стороны он
     * не содержит клеток веерного движения — хотя ходить с них можно и фигуры на них
     * нарисованы. Захват такой клетки должен давать 30 очков, а не 10.
     * Вне хода стороны оба набора совпадают (см. endTurn/startNewRound).
     */
    isActiveField(position) {
        return this._turnActiveFields.has(position.toKey());
    }
    /**
     * Установка активного поля
     */
    setActiveField(position, active) {
        const key = position.toKey();
        if (active) {
            this._activeFields.add(key);
        }
        else {
            this._activeFields.delete(key);
        }
    }
    /**
     * Добавление поля в активные для текущего раунда
     */
    addTurnActiveField(position) {
        this._turnActiveFields.add(position.toKey());
    }
    /**
     * Удаление поля из активных для текущего раунда
     * FIX BUG-8: Необходимо для удаления выжженных территорий
     */
    removeTurnActiveField(position) {
        this._turnActiveFields.delete(position.toKey());
    }
    /**
     * Установка последней позиции хода
     */
    setLastMovePosition(position) {
        this._lastMovePosition = position;
        // При новом ходе очищаем список захваченных с предыдущего хода
        this._capturedOnLastMove.clear();
    }
    /**
     * Добавление территории захваченной на последнем ходу
     */
    addCapturedOnLastMove(position) {
        this._capturedOnLastMove.add(position.toKey());
    }
    /**
     * Очистка захваченных территорий на последнем ходу (для нового хода)
     */
    clearCapturedOnLastMove() {
        this._capturedOnLastMove.clear();
    }
    /**
     * Records a territory received via trap resolution.
     * Unlike _capturedOnLastMove, this set is NOT cleared by setLastMovePosition,
     * so it survives the recipient's own moves within the same round and is
     * reliably processed by endTurn() / startNewRound().
     */
    addTrapCapturedField(position) {
        this._trapCapturedFields.add(position.toKey());
    }
    /**
     * НОВОЕ: Завершение хода стороны
     * Очищает turnActiveFields до только lastMovePosition + capturedOnLastMove
     * Вызывается когда сторона заканчивает свой ход (переходит к другой стороне)
     */
    endTurn() {
        console.log(`[Side ${this._id}] endTurn - clearing turnActiveFields to only last move`);
        console.log(`  - BEFORE: turnActiveFields = ${Array.from(this._turnActiveFields)}`);
        console.log(`  - lastMovePosition = ${this._lastMovePosition?.toString() || 'none'}`);
        console.log(`  - capturedOnLastMove = ${Array.from(this._capturedOnLastMove)}`);
        // Очищаем both active field sets to keep them in sync
        this._turnActiveFields.clear();
        this._activeFields.clear();
        // Добавляем только lastMovePosition + capturedOnLastMove
        if (this._lastMovePosition) {
            const key = this._lastMovePosition.toKey();
            if (this._territories.has(key)) {
                this._turnActiveFields.add(key);
                this._activeFields.add(key);
            }
        }
        for (const capturedKey of this._capturedOnLastMove) {
            if (this._territories.has(capturedKey)) {
                this._turnActiveFields.add(capturedKey);
                this._activeFields.add(capturedKey);
            }
        }
        // Include territories received via trap resolution this round (not cleared by setLastMovePosition)
        for (const trapKey of this._trapCapturedFields) {
            if (this._territories.has(trapKey)) {
                this._turnActiveFields.add(trapKey);
                this._activeFields.add(trapKey);
            }
        }
        // Clear here so startNewRound() sees them (it runs BEFORE auto-skip's endTurn()),
        // and after startNewRound() has already processed them, they are gone.
        // FIX: was cleared in startNewRound() BEFORE auto-skip could call endTurn(),
        // causing trap-received territories to vanish from _turnActiveFields.
        this._trapCapturedFields.clear();
        console.log(`  - AFTER: turnActiveFields = ${Array.from(this._turnActiveFields)}`);
    }
    /**
     * Добавление отложенной ловушки (ESCAPABLE trap)
     * Ловушка будет разрешена в конце раунда, если игрок не сбежит
     */
    addPendingTrap(trap) {
        const key = trap.position.toKey();
        this._pendingTraps.set(key, trap);
        console.log(`[Side ${this._id}] Added pending trap at ${key} -> give to side ${trap.giveTerritoryTo}`);
    }
    /**
     * Удаление отложенной ловушки (игрок сбежал - разомкнул окружение)
     */
    removePendingTrap(position) {
        const key = position.toKey();
        const removed = this._pendingTraps.delete(key);
        if (removed) {
            console.log(`[Side ${this._id}] Removed pending trap at ${key} (escaped!)`);
        }
        return removed;
    }
    /**
     * Получение всех отложенных ловушек
     */
    getPendingTraps() {
        return Array.from(this._pendingTraps.values());
    }
    /**
     * Проверка наличия отложенных ловушек
     */
    hasPendingTraps() {
        return this._pendingTraps.size > 0;
    }
    /**
     * Очистка всех отложенных ловушек (в конце раунда после разрешения)
     */
    clearPendingTraps() {
        this._pendingTraps.clear();
    }
    // ── Pending Group Conquests ──────────────────────────────────────────────
    /**
     * Добавить отложенный групповой захват.
     * Вызывается когда группа полностью окружена, но у игрока ещё есть ходы.
     */
    addPendingGroupConquest(conquest) {
        this._pendingGroupConquests.push(conquest);
        console.log(`[Side ${this._id}] Added pending group conquest: ${conquest.positions.length} territories -> side ${conquest.giveTerritoryTo}`);
    }
    /**
     * Получить все отложенные групповые захваты
     */
    getPendingGroupConquests() {
        return [...this._pendingGroupConquests];
    }
    /**
     * Проверить наличие отложенных групповых захватов
     */
    hasPendingGroupConquests() {
        return this._pendingGroupConquests.length > 0;
    }
    /**
     * Удалить конкретный отложенный групповой захват (группа вышла из окружения)
     */
    removePendingGroupConquest(conquest) {
        const keys = new Set(conquest.positions.map(p => p.toKey()));
        this._pendingGroupConquests = this._pendingGroupConquests.filter(pc => !pc.positions.some(p => keys.has(p.toKey())));
    }
    /**
     * Очистить все отложенные групповые захваты (после разрешения)
     */
    clearPendingGroupConquests() {
        this._pendingGroupConquests = [];
    }
    /**
     * Инициализация стороны
     * @param board - Опциональная ссылка на Board для установки owner начальной территории
     */
    initialize(board) {
        this._status = SideStatus.ACTIVE;
        this._movesInCurrentRound = 0;
        // Set up active fields from territories (start position)
        const startPos = SIDE_START_POSITIONS[this._id];
        if (startPos) {
            const key = startPos.toKey();
            this._activeFields.add(key);
            // КРИТИЧНО: Добавляем начальную территорию в _territories
            // Без этого startNewRound() не найдёт территорию и turnActiveFields будет пустым
            this._territories.add(key);
            this._territoriesControlled.add(key);
            // ВАЖНО: Устанавливаем owner в Territory, чтобы начальная территория правильно учитывалась
            // Устанавливаем previousOwner = sideId чтобы ScoreCalculator понимал что это начальная территория
            if (board) {
                const territory = board.getTerritoryAt(startPos);
                if (territory && territory.owner === null) {
                    // Напрямую устанавливаем owner и previousOwner
                    // previousOwner = sideId означает "это всегда была моя территория" → не даст очков в ScoreCalculator
                    territory['_owner'] = this._id;
                    territory['_previousOwner'] = this._id; // Указываем что это начальная территория (owner === previousOwner)
                    territory['_state'] = TerritoryState.OWNED;
                }
            }
        }
        this._turnActiveFields = new Set(this._activeFields);
    }
    /**
     * Начало нового раунда
     */
    startNewRound() {
        this._movesInCurrentRound = 0;
        // Логируем состояние ДО очистки
        console.log(`[Side ${this._id}] startNewRound - BEFORE clear:`);
        console.log(`  - turnActiveFields: ${Array.from(this._turnActiveFields)}`);
        console.log(`  - lastMovePosition: ${this._lastMovePosition?.toString() || 'none'}`);
        console.log(`  - capturedOnLastMove: ${Array.from(this._capturedOnLastMove)}`);
        // Очищаем активные поля для нового раунда
        this._activeFields.clear();
        this._turnActiveFields.clear();
        // Активными становятся только:
        // 1. Последняя позиция хода из предыдущего раунда
        if (this._lastMovePosition) {
            const lastPosKey = this._lastMovePosition.toKey();
            // ВАЖНО: Добавляем только если территория принадлежит нам
            if (this._territories.has(lastPosKey)) {
                this._activeFields.add(lastPosKey);
                this._turnActiveFields.add(lastPosKey);
                console.log(`[Side ${this._id}] Added last move position: ${lastPosKey}`);
            }
            else {
                console.warn(`[Side ${this._id}] Last move position ${lastPosKey} is not owned, skipping`);
            }
        }
        // 2. Территории, захваченные окружением на последнем ходу предыдущего раунда
        for (const capturedKey of this._capturedOnLastMove) {
            // ВАЖНО: Добавляем только если территория принадлежит нам
            if (this._territories.has(capturedKey)) {
                this._activeFields.add(capturedKey);
                this._turnActiveFields.add(capturedKey);
                console.log(`[Side ${this._id}] Added captured territory: ${capturedKey}`);
            }
            else {
                console.warn(`[Side ${this._id}] Captured territory ${capturedKey} is not owned, skipping`);
            }
        }
        // 3. Territories received via trap resolution (survived across moves in previous round)
        for (const trapKey of this._trapCapturedFields) {
            if (this._territories.has(trapKey)) {
                this._activeFields.add(trapKey);
                this._turnActiveFields.add(trapKey);
                console.log(`[Side ${this._id}] Added trap-captured territory: ${trapKey}`);
            }
            else {
                console.warn(`[Side ${this._id}] Trap-captured territory ${trapKey} is not owned, skipping`);
            }
        }
        // Логируем состояние ПОСЛЕ установки
        console.log(`[Side ${this._id}] startNewRound - AFTER setup:`);
        console.log(`  - turnActiveFields: ${Array.from(this._turnActiveFields)}`);
        // Очищаем список захваченных для нового раунда
        this._capturedOnLastMove.clear();
        // NOTE: _trapCapturedFields is NOT cleared here intentionally.
        // It is cleared in endTurn() AFTER processing, so that if auto-skip calls
        // endTurn() right after startNewRound(), the trap fields survive and are
        // correctly added back to _turnActiveFields before being cleared.
    }
    /**
     * Выполнение хода
     */
    makeMove() {
        this._movesInCurrentRound++;
    }
    /**
     * Элиминация стороны
     */
    eliminate() {
        this._status = SideStatus.ELIMINATED;
        this._activeFields.clear();
        this._turnActiveFields.clear();
    }
    /**
     * Форфейт стороны — как элиминация, но БЕЗ права на реанимацию
     * (игрок сдался или выбыл по неактивности). Территории сохраняются ("заморожены").
     */
    forfeit() {
        this.eliminate();
        this._permanentlyOut = true;
    }
    /**
     * Суспендирование стороны (выбытие с возможностью реанимации)
     */
    suspend() {
        if (this._status !== SideStatus.ACTIVE && this._status !== SideStatus.REANIMATED) {
            return;
        }
        console.log(`[Side ${this._id}] Suspending - no moves available but has ${this._territories.size} territories`);
        this._status = SideStatus.SUSPENDED;
        // Очищаем активные поля, но сохраняем территории
        this._turnActiveFields.clear();
        // activeFields тоже очищаем, так как сторона не может ходить
        this._activeFields.clear();
    }
    /**
     * Реанимация стороны (возврат в игру)
     */
    reanimate(position) {
        // Может реанимировать из SUSPENDED или ELIMINATED (для обратной совместимости)
        if (this._status === SideStatus.ACTIVE || this._status === SideStatus.REANIMATED) {
            return;
        }
        const wasEliminated = this._status === SideStatus.ELIMINATED;
        console.log(`[Side ${this._id}] Reanimating from ${this._status} status`);
        this._status = SideStatus.ACTIVE; // Используем ACTIVE вместо REANIMATED
        if (position) {
            const key = position.toKey();
            // Если была элиминирована, восстанавливаем территорию
            if (wasEliminated) {
                this._territories.add(key);
            }
            // При реанимации активной становится ТОЛЬКО переданная позиция
            this._activeFields.clear();
            this._turnActiveFields.clear();
            this._activeFields.add(key);
            this._turnActiveFields.add(key);
            this._lastMovePosition = position;
        }
        // Если позиция не передана, активные поля остаются пустыми
        // (сторона не может ходить пока не получит территорию через ловушку)
    }
    /**
     * Проверка, может ли сторона сделать ход
     */
    canMove() {
        return this.isActive && this._activeFields.size > 0;
    }
    /**
     * Получение количества оставшихся ходов в раунде
     * @deprecated Используйте RoundManager.getRemainingMoves() для единообразия
     */
    getRemainingMoves(round, maxMovesPerRound) {
        // Логика перенесена в RoundManager.getMovesForSide()
        // Этот метод оставлен для обратной совместимости
        let allowedMoves = maxMovesPerRound;
        if (this._id === 1 && round === 2) {
            allowedMoves = 2; // Сторона 1 получает 2 хода в раунде 2
        }
        // В раунде 3 и далее все стороны получают 3 хода
        return Math.max(0, allowedMoves - this._movesInCurrentRound);
    }
    /**
     * Клонирование стороны
     */
    clone() {
        const cloned = new Side(this._id, this._playerId, this._isBot);
        cloned._status = this._status;
        cloned._territories = new Set(this._territories);
        cloned._activeFields = new Set(this._activeFields);
        cloned._turnActiveFields = new Set(this._turnActiveFields);
        cloned._movesInCurrentRound = this._movesInCurrentRound;
        cloned._lastMovePosition = this._lastMovePosition
            ? new Position(this._lastMovePosition.x, this._lastMovePosition.y)
            : null;
        cloned._capturedOnLastMove = new Set(this._capturedOnLastMove);
        cloned._trapCapturedFields = new Set(this._trapCapturedFields);
        // Клонируем отложенные ловушки
        for (const [key, trap] of this._pendingTraps) {
            cloned._pendingTraps.set(key, {
                ...trap,
                position: new Position(trap.position.x, trap.position.y),
            });
        }
        // Клонируем отложенные групповые захваты
        cloned._pendingGroupConquests = this._pendingGroupConquests.map(gc => ({
            ...gc,
            positions: gc.positions.map(p => new Position(p.x, p.y)),
        }));
        return cloned;
    }
    /**
     * Экспорт данных стороны
     */
    toData() {
        return {
            id: this._id,
            playerId: this._playerId,
            status: this._status,
            territories: new Set(this._territories),
            territoriesControlled: new Set(this._territoriesControlled),
            activeFields: new Set(this._activeFields),
            turnActiveFields: new Set(this._turnActiveFields),
            movesInCurrentRound: this._movesInCurrentRound,
            totalTerritories: this._territories.size,
            isBot: this._isBot,
        };
    }
    /**
     * Сериализация в JSON
     */
    toJSON() {
        return {
            id: this._id,
            playerId: this._playerId,
            status: this._status,
            permanentlyOut: this._permanentlyOut,
            territories: Array.from(this._territories),
            activeFields: Array.from(this._activeFields),
            turnActiveFields: Array.from(this._turnActiveFields),
            movesInCurrentRound: this._movesInCurrentRound,
            isBot: this._isBot,
            lastMovePosition: this._lastMovePosition
                ? {
                    x: this._lastMovePosition.x,
                    y: this._lastMovePosition.y,
                }
                : null,
            capturedOnLastMove: Array.from(this._capturedOnLastMove),
            trapCapturedFields: Array.from(this._trapCapturedFields),
            pendingTraps: Array.from(this._pendingTraps.values()).map(trap => ({
                position: { x: trap.position.x, y: trap.position.y },
                giveTerritoryTo: trap.giveTerritoryTo,
                surroundingSides: trap.surroundingSides,
                createdAtMove: trap.createdAtMove,
            })),
        };
    }
    /**
     * Восстановление из JSON
     */
    static fromJSON(data) {
        const side = new Side(data.id, data.playerId, data.isBot);
        side._status = data.status;
        side._permanentlyOut = data.permanentlyOut ?? false;
        side._territories = new Set(data.territories);
        side._activeFields = new Set(data.activeFields);
        side._turnActiveFields = new Set(data.turnActiveFields);
        side._movesInCurrentRound = data.movesInCurrentRound;
        if (data.lastMovePosition) {
            // Using the imported Position class at top of file
            side._lastMovePosition = new Position(data.lastMovePosition.x, data.lastMovePosition.y);
        }
        side._capturedOnLastMove = new Set(data.capturedOnLastMove || []);
        side._trapCapturedFields = new Set(data.trapCapturedFields || []);
        // Восстанавливаем отложенные ловушки
        if (data.pendingTraps && Array.isArray(data.pendingTraps)) {
            for (const trapData of data.pendingTraps) {
                const position = new Position(trapData.position.x, trapData.position.y);
                side._pendingTraps.set(position.toKey(), {
                    position,
                    giveTerritoryTo: trapData.giveTerritoryTo,
                    surroundingSides: trapData.surroundingSides,
                    createdAtMove: trapData.createdAtMove,
                });
            }
        }
        return side;
    }
    toString() {
        return `Side[${this._id}, status=${this._status}, territories=${this._territories.size}, activeFields=${this._activeFields.size}]`;
    }
}
//# sourceMappingURL=Side.js.map